import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Reveals any element carrying `data-reveal` as it scrolls into view.
 * Runs once per route so newly rendered sections are picked up on navigation,
 * and degrades to "everything visible" when the browser lacks IntersectionObserver
 * or the visitor prefers reduced motion.
 */
const useScrollReveal = () => {
  const location = useLocation();

  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll("[data-reveal]"));
    if (!nodes.length) return undefined;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      nodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [location.pathname]);
};

export default useScrollReveal;
